import { Insight, InsightType } from '../../services/insightsService';

// Visual identity per insight type. Unknown types fall back to a neutral style
// so "coming soon" detectors render without code changes.
interface TypeMeta {
  label: string;
  emoji: string;
  color: string; // accent color (dot / left border)
}

const TYPE_META: Record<string, TypeMeta> = {
  daily_recap: { label: 'Recap diario', emoji: '📅', color: 'var(--info)' },
  supplement_lapse: { label: 'Suplemento omitido', emoji: '💊', color: 'var(--warning)' },
};

export const getTypeMeta = (type: InsightType): TypeMeta =>
  TYPE_META[type] || { label: type, emoji: '🔎', color: 'var(--muted-foreground)' };

// Relative time in Spanish (e.g. "hace 2 h", "ayer").
export const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

// A compact list of key/value metrics derived from the payload for known types.
// Returns [] for unknown types — the summary sentence already carries the meaning.
export const getPayloadMetrics = (insight: Insight): { label: string; value: string }[] => {
  const p = insight.payload || {};
  const num = (v: any) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));

  if (insight.type === 'daily_recap') {
    const metrics: { label: string; value: string }[] = [];
    if (p.items_total != null) metrics.push({ label: 'Items', value: `${p.items_marked ?? 0}/${p.items_total}` });
    if (p.protein_g != null) {
      const target = p.protein_target_g != null ? ` / ${Math.round(num(p.protein_target_g)!)}` : '';
      metrics.push({ label: 'Proteína', value: `${Math.round(num(p.protein_g)!)}${target} g` });
    }
    if (p.kcal != null) {
      const target = p.kcal_target != null ? ` / ${Math.round(num(p.kcal_target)!)}` : '';
      metrics.push({ label: 'Energía', value: `${Math.round(num(p.kcal)!)}${target} kcal` });
    }
    if (p.sleep && p.sleep.total_min != null) {
      const h = Math.floor(num(p.sleep.total_min)! / 60);
      const m = Math.round(num(p.sleep.total_min)! % 60);
      const eff = p.sleep.efficiency_pct != null ? ` · ${Math.round(num(p.sleep.efficiency_pct)!)}%` : '';
      metrics.push({ label: 'Sueño', value: `${h}h ${m}m${eff}` });
    }
    if (p.today_items_ahead != null) metrics.push({ label: 'Hoy', value: `${p.today_items_ahead} pendientes` });
    return metrics;
  }

  if (insight.type === 'supplement_lapse') {
    const metrics: { label: string; value: string }[] = [];
    if (p.streak_days != null) metrics.push({ label: 'Días seguidos', value: String(p.streak_days) });
    if (p.scheduled_per_week != null) metrics.push({ label: 'Pauta', value: `~${p.scheduled_per_week}×/sem` });
    if (p.purpose) metrics.push({ label: 'Propósito', value: String(p.purpose) });
    if (p.goal) metrics.push({ label: 'Objetivo', value: String(p.goal) });
    return metrics;
  }

  return [];
};
